import { capitalize } from 'lodash';
import moment from 'moment';

import { billingPeriod } from 'utilities/billing_period';

import { BillingCycle, PaymentMethod, Pricing, SubscriptionPlan } from './types';

// Calculates current year, list of years and list of months
export const year = new Date().getFullYear();
export const years = Array.from(new Array(21), (val, index) => index + year);
export const months = Array.from(Array(12).keys(), (val, index) => index + 1);

// Calculates the expiration date based on the plan's duration
export const calculateExpirationDate = (subscriptionPlan: SubscriptionPlan) => {
  return moment()
    .add(subscriptionPlan.durationYears, 'y')
    .add(subscriptionPlan.durationMonths, 'M')
    .add(subscriptionPlan.durationDays, 'd')
    .format('LL');
};

export { billingPeriod };

// Calculates subtotal price
export const calculateSubtotal = (price, ammount) => {
  return price * ammount;
};

// Sums prices
export const calculateSum = (arr) => {
  return arr.reduce((sum, x) => sum + x);
};

// Matches Installments::INSTALLMENT_ITERATIONS
export const installmentIterations = 4;

// Calculates price of each installment. Partial cents are dropped, the same as the integer
// division the backend charges installments with.
export const calculateInstallmentsPrice = (price) => {
  return Math.floor(price / installmentIterations);
};

// Calculates installments payment date
export const calculateInstallmentsDates = (interval: 'week' | 'month', intervalCount: number) => {
  const dates = [
    'today',
    moment().add(intervalCount, interval).format('MM/DD'),
    moment().add(intervalCount * 2, interval).format('MM/DD'),
    moment().add(intervalCount * 3, interval).format('MM/DD')
  ];

  return dates;
};

export const isPaidOrder = (pricing: Pricing) => {
 return pricing.total > 0;
};

// Paying in installments is its own payment method rather than a style applied to another one,
// but it is always collected by card, so it belongs anywhere a card payment does.
export const isCardPayment = (paymentMethod: PaymentMethod) => {
  return paymentMethod === PaymentMethod.STRIPE;
};

export const isInstallments = (billingCycle: BillingCycle) => {
  return billingCycle === BillingCycle.INSTALLMENTS;
};

// How a plan bills when the student hasn't chosen installments. A recurring plan can only be
// bought as recurring.
export const planBillingCycle = (subscriptionPlan: SubscriptionPlan) => {
  return subscriptionPlan.recurring ? BillingCycle.RECURRING : BillingCycle.ONE_TIME;
};

// Installments and renewals are billed to a card Stripe keeps on file
export const billsRepeatedly = (billingCycle: BillingCycle) => {
  return billingCycle !== BillingCycle.ONE_TIME;
};

// The ways to pay that leave Stripe a card to bill again: one typed in, or one a browser wallet
// hands over. Matches PaymentTerms::REPEAT_BILLING_PAYMENT_METHODS.
export const billsCardOnFile = (paymentMethod: PaymentMethod) => {
  return paymentMethod === PaymentMethod.STRIPE || paymentMethod === PaymentMethod.STRIPE_PAYMENT_REQUEST;
};

// What a browser wallet's sheet asks the student to pay today
export const walletChargeToday = (billingCycle: BillingCycle, pricing: Pricing) => {
  return isInstallments(billingCycle) ? calculateInstallmentsPrice(pricing.total) : pricing.total;
};

// A plan's length as the single unit Apple Pay bills by, or null when it takes more than one
const renewalInterval = (plan: SubscriptionPlan): { count: number; unit: 'day' | 'month' | 'year' } => {
  const { durationDays: days, durationMonths: months, durationYears: years } = plan;

  if (days > 0) {
    return months === 0 && years === 0 ? { count: days, unit: 'day' } : null;
  } else if (months > 0) {
    return { count: years * 12 + months, unit: 'month' };
  } else if (years > 0) {
    return { count: years, unit: 'year' };
  } else {
    return null;
  }
};

// Apple Pay's description of the charges that follow today's, for a wallet billed again. Apple
// shows it on the sheet and in the wallet, and it's what earns a merchant token that outlives the
// student's device. Nothing to describe for a single payment.
export const applePayRecurringPaymentRequest = (
  subscriptionPlan: SubscriptionPlan,
  billingCycle: BillingCycle,
  pricing: Pricing,
  managementURL: string
) => {
  if (isInstallments(billingCycle)) {
    const { installmentInterval: unit, installmentIntervalCount: count } = subscriptionPlan;
    if (!unit || !count) return null;

    return {
      recurringPaymentRequest: {
        managementURL,
        paymentDescription: `${subscriptionPlan.name}, paid in ${installmentIterations} installments`,
        regularBilling: {
          amount: calculateInstallmentsPrice(pricing.total),
          label: `${subscriptionPlan.name} installment`,
          recurringPaymentEndDate: moment().add(count * (installmentIterations - 1), unit).toDate(),
          recurringPaymentIntervalCount: count,
          recurringPaymentIntervalUnit: unit,
          recurringPaymentStartDate: moment().toDate()
        }
      }
    };
  } else if (billingCycle === BillingCycle.RECURRING) {
    const interval = renewalInterval(subscriptionPlan);
    if (!interval) return null;

    const renewalAmount = pricing.renewalTotal != null ? pricing.renewalTotal : pricing.total;
    const firstRenewal = moment().add(interval.count, interval.unit).toDate();

    return {
      recurringPaymentRequest: {
        managementURL,
        paymentDescription: `${subscriptionPlan.name}, renewing every ${billingPeriod(subscriptionPlan)}`,
        // A discount on the first period only, like a coupon used once: Apple shows it as an
        // introductory price ahead of the regular one, rather than as an unexplained first charge
        ...(pricing.total !== renewalAmount ? {
          trialBilling: {
            amount: pricing.total,
            label: `${subscriptionPlan.name}, first ${billingPeriod(subscriptionPlan)}`,
            recurringPaymentEndDate: firstRenewal,
            recurringPaymentIntervalCount: interval.count,
            recurringPaymentIntervalUnit: interval.unit,
            recurringPaymentStartDate: moment().toDate()
          }
        } : {}),
        regularBilling: {
          amount: renewalAmount,
          label: `${subscriptionPlan.name} renewal`,
          recurringPaymentIntervalCount: interval.count,
          recurringPaymentIntervalUnit: interval.unit,
          recurringPaymentStartDate: firstRenewal
        }
      }
    };
  } else {
    return null;
  }
};

// Every way to pay on checkout is taxed by the billing ZIP, so each collects the student's name
// and billing ZIP: a card, a browser wallet, PayPal and Alipay. Matches
// PaymentTerms::SALES_TAXED_PAYMENT_METHODS.
export const collectsBillingDetails = (paymentMethod: PaymentMethod) => {
  return billsCardOnFile(paymentMethod) ||
    paymentMethod === PaymentMethod.PAYPAL ||
    paymentMethod === PaymentMethod.ALIPAY;
};

export const shouldValidatePaymentInfo = (pricing, paymentMethod, schema) => {
  if (collectsBillingDetails(paymentMethod) && isPaidOrder(pricing)) {
    return schema.required();
  } else {
    return schema.optional();
  }
};
